import { useCallback, useSyncExternalStore } from "react";

/**
 * A CSS breakpoint, readable from JS.
 *
 * Layout is CSS's job here and almost always stays CSS's job (`adm:` renders
 * one branch or the other). This exists for the handful of places where the
 * two shapes must not both EXIST, not merely not both show:
 *
 * - a heavy Base UI listbox rendered twice is two listboxes in the bundle's
 *   work and two mounted subscriptions, for one control;
 * - a subtree behind `display: none` is the trap that once put dialogs at zero
 *   width and made desktop editing silently impossible (`src/ui/README.md` §5);
 * - and `StreamPanel` polls the OBS bridge while mounted, so a hidden second
 *   copy would double it.
 */

/** The admin breakpoint, as a media query. Mirrors `--breakpoint-adm` (60rem)
 *  in `theme.css`: CSS owns the number, and this is the one place JS repeats
 *  it rather than every call site inventing its own. */
export const ADM_QUERY = "(min-width: 60rem)";

/** One `MediaQueryList` per query, so `subscribe` is stable and every consumer
 *  of the same breakpoint shares one listener. */
const lists = new Map<string, MediaQueryList>();

function listFor(query: string): MediaQueryList {
  let list = lists.get(query);
  if (list === undefined) {
    list = window.matchMedia(query);
    lists.set(query, list);
  }
  return list;
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = listFor(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => listFor(query).matches,
    // No SSR in this app; a snapshot is still required, and "narrow" is the
    // safer guess because the phone layout renders correctly at any width.
    () => false,
  );
}
